import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DatabaseZap, Loader2, Star, Check } from "lucide-react";
import { icons as lucideIcons } from "lucide-react";
import { motion } from "framer-motion";
import { getNewData } from "../../../../api/home";

/* ── Render a lucide icon by string name ── */
const DynamicIcon = ({
  name,
  size = 14,
  strokeWidth = 2,
  style,
  className,
}) => {
  // Convert kebab-case to PascalCase for lucide lookup
  const pascal = name
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
  const IconComp = lucideIcons[pascal];
  if (!IconComp) return null;
  return (
    <IconComp
      size={size}
      strokeWidth={strokeWidth}
      style={style}
      className={className}
    />
  );
};

/* ── Single row item ── */
const DataRow = ({ item, dark, t, isFavorite, onClick }) => {
  const borderColor = dark ? "rgba(14,165,233,0.06)" : "rgba(203,213,225,0.25)";
  const iconColor = item.color || t.textMuted;

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors hover:bg-white/[0.02] cursor-pointer"
      style={{ border: `1px solid ${borderColor}` }}
    >
      {/* Icon */}
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
        style={{ background: iconColor + "15" }}
      >
        {item.icon ? (
          <DynamicIcon
            name={item.icon}
            size={12}
            strokeWidth={2}
            style={{ color: iconColor }}
          />
        ) : isFavorite ? (
          <Star
            size={12}
            strokeWidth={2}
            style={{ color: iconColor, fill: iconColor }}
          />
        ) : (
          <div
            className="w-2 h-2 rounded-full"
            style={{ background: iconColor }}
          />
        )}
      </div>

      {/* Label */}
      <span
        className="flex-1 text-[11px] font-medium truncate"
        style={{ color: t.textPrimary }}
      >
        {item.label}
      </span>

      {/* Count badge */}
      <span
        className="text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded-full min-w-[24px] text-center"
        style={{
          background: iconColor + "18",
          color: iconColor,
        }}
      >
        {item.count > 999 ? "999+" : item.count}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   NewDataPanel
   ═══════════════════════════════════════════════════════════════ */
const NewDataPanel = ({ dark, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getNewData()
      .then((res) => {
        if (res?.data?.status === 200) {
          setData(res.data);
        } else {
          setError(res?.data?.message || "Failed to load");
        }
      })
      .catch((e) => setError(e?.message || "Failed"))
      .finally(() => setLoading(false));
  }, []);

  const totalNew = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const s of data.system_views || [])
      if (s.key !== "Global") total += s.count || 0;
    for (const s of data.custom_views || []) total += s.count || 0;
    for (const s of data.favorited_sources || []) total += s.count || 0;
    return total;
  }, [data]);

  /* Combine all items, filter out zeros, sort descending by count, tag with kind */
  const combinedItems = useMemo(() => {
    if (!data) return [];
    const tag = (arr, kind) =>
      (arr || [])
        .filter((v) => (v.count || 0) > 0)
        .filter((v) => !(kind === "system" && v.key === "Global"))
        .map((v) => ({ ...v, _kind: kind, _data: v }));
    return [
      ...tag(data.custom_views, "custom"),
      ...tag(data.favorited_sources, "favorited_source"),
      ...tag(data.system_views, "system"),
    ].sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [data]);

  /* ── Navigate to the corresponding feed view ── */
  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (item) => {
      navigate("/feed");

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "feed" },
        }),
      );

      const d = item._data;
      let detail;
      if (item._kind === "system") {
        detail = { kind: "system", view: d };
      } else if (item._kind === "favorited_source") {
        detail = {
          kind: "favorited_source",
          document_id: d.document_id,
          source: d.source_value,
        };
      } else {
        detail = { kind: "custom", view: d };
      }

      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("sidebar:feed-switch-view", { detail }),
        );
      }, 50);
    },
    [navigate],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 shrink-0"
        style={{ borderBottom: `1px solid ${t.cardBorder}` }}
      >
        <div className="flex items-center gap-2">
          <DatabaseZap
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: t.textSecondary }}
            strokeWidth={2}
          />
          <span
            className="text-[12px] font-medium"
            style={{ color: t.textPrimary }}
          >
            New Data
          </span>
          {totalNew > 0 && !loading && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full tabular-nums"
              style={{
                background: "rgba(16,185,129,0.15)",
                color: "rgba(16,185,129,0.9)",
              }}
            >
              {totalNew > 999 ? "999+" : totalNew}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto px-3 py-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {loading ? (
          <div
            className="h-full flex items-center justify-center gap-2"
            style={{ color: t.textMuted }}
          >
            <Loader2
              size={14}
              className="animate-spin"
              style={{ color: t.activeText }}
            />
            <span className="text-[11px]">Loading...</span>
          </div>
        ) : error ? (
          <div
            className="h-full flex items-center justify-center text-[11px]"
            style={{ color: t.dangerText }}
          >
            {error}
          </div>
        ) : (
          (() => {
            let rowIndex = 0;
            const animRow = (key, item) => {
              const i = rowIndex++;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 6, filter: "blur(2px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.25,
                    delay: i * 0.04,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <DataRow
                    item={item}
                    dark={dark}
                    t={t}
                    isFavorite={item._kind === "favorited_source"}
                    onClick={() => handleItemClick(item)}
                  />
                </motion.div>
              );
            };
            return combinedItems.length > 0 ? (
              <div className="space-y-1">
                {combinedItems.map((v) => animRow(v.key, v))}
              </div>
            ) : (
              <div
                className="h-full flex flex-col items-center justify-center gap-2"
                style={{ color: t.textMuted }}
              >
                <Check size={20} style={{ opacity: 0.3 }} />
                <span className="text-[11px]">All caught up</span>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default NewDataPanel;
